import { useState } from 'react'
import { useNavigate } from 'react-router'
import { ArrowRight, Check, CreditCard, FileText } from 'lucide-react'
import { PageHeader, CountryPicker } from '@/components/common'
import { EmployeeDocumentsTab } from '@/features/document'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DatePicker } from '@/components/ui/date-picker'
import { Input } from '@/components/ui/input'
import {
  useCostCenters, useDepartments, useDesignations, useDivisions, useEstablishments,
  useGrades, useLegalEntities, useLocations,
} from '@/features/org'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { toApiProblem } from '@/lib/api/client'
import { formProblem } from '@/lib/api/fieldErrors'
import { cn } from '@/lib/utils'
import { useBankAccountMutations, useEmployeeMutations, useEmployeeOptions } from '../api'
import { ChoiceField, Field, OptionField, TextField } from '../components/EmployeeFields'
import { EMPLOYMENT_TYPES, GENDERS, pruned } from '../components/employeeFieldOptions'

const STEPS = ['Person', 'Employment', 'First placement', 'Documents', 'Bank'] as const

/**
 * Adding somebody.
 *
 * <p>Until now this product could show an employee and never create one: the directory was
 * read-only and the only way a person reached the database was the demo seed or a SQL
 * statement. This is the screen that gap was about.
 *
 * <p><b>Why five steps and not one form.</b> A new joiner is four separate decisions made by
 * up to three people — who they are, what their contract says, where they sit, and what
 * paperwork is on file — and a single scrolling form of thirty fields makes the required ones
 * (department and join date) look no more important than the optional twenty-eight.
 *
 * <p><b>Why the employee is created at step three rather than at the end.</b> Documents and
 * bank accounts hang off an employee id, so they cannot be posted before there is one. Making
 * them steps four and five, after the record exists, means a failure while attaching a
 * passport scan costs the passport scan — not the joiner, their employment and their
 * placement as well. The person is in the directory from step three onwards, and both
 * remaining steps say plainly that they can be skipped and done later from the profile.
 */
/** What the request calls a field, in the words the form uses for it. */
const FIELD_LABELS: Record<string, string> = {
  firstName: 'First name', middleName: 'Middle name', lastName: 'Last name',
  workEmail: 'Work email', workPhone: 'Work phone', nationality: 'Nationality',
  personalEmail: 'Personal email', personalPhone: 'Personal phone',
  nationalIdType: 'National ID type', nationalId: 'National ID', passportNo: 'Passport number',
  employeeCode: 'Employee code', joinDate: 'Join date', departmentId: 'Department',
  contractType: 'Contract type', contractEndDate: 'Contract end date',
}

export function AddEmployeePage() {
  const breadcrumb = useBreadcrumb('Add employee')
  const navigate = useNavigate()

  const [step, setStep] = useState(0)
  const [createdId, setCreatedId] = useState<string | null>(null)
  const [createdName, setCreatedName] = useState('')

  const entities = useLegalEntities()
  const establishments = useEstablishments()
  const departments = useDepartments()
  const divisions = useDivisions()
  const designations = useDesignations()
  const grades = useGrades()
  const locations = useLocations()
  const costCenters = useCostCenters()
  const managers = useEmployeeOptions()

  const { create } = useEmployeeMutations()
  const bank = useBankAccountMutations(createdId ?? undefined)

  const [person, setPerson] = useState({
    firstName: '', middleName: '', lastName: '', gender: '', dateOfBirth: '',
    nationality: '', nationalIdType: '', nationalId: '', passportNo: '',
    personalEmail: '', personalPhone: '',
  })
  const [employment, setEmployment] = useState({
    employeeCode: '', workEmail: '', workPhone: '', legalEntityId: '', establishmentId: '',
    joinDate: '', employmentType: 'FULL_TIME', contractType: '', contractEndDate: '',
    probationMonths: '',
  })
  const [placement, setPlacement] = useState({
    departmentId: '', divisionId: '', designationId: '', gradeId: '', locationId: '',
    costCenterId: '', managerId: '',
  })
  const [account, setAccount] = useState({
    bankName: '', accountHolderName: '', iban: '', accountNumber: '', swiftCode: '',
    routingCode: '', currency: '',
  })
  const [bankError, setBankError] = useState<string | null>(null)
  const [bankSaved, setBankSaved] = useState(false)

  // The employee is created on the placement step, but most of what the server can refuse —
  // a nationality, an email — was typed two steps earlier. So each message goes under its own
  // field, for whoever goes back to fix it, and is also listed here by the field's name, since
  // the field itself is not on screen.
  const createProblem = create.isError
    ? formProblem(create.error, 'The employee could not be created.')
    : null
  const fieldErrors = createProblem?.fields ?? {}
  const createError = createProblem?.summary ?? null

  const canLeavePerson = person.firstName.trim() !== ''
  const canLeaveEmployment = employment.joinDate !== ''
  const canCreate = placement.departmentId !== ''

  async function createEmployee() {
    const detail = await create.mutateAsync(pruned({
      ...person,
      ...employment,
      ...placement,
      probationMonths: employment.probationMonths === ''
        ? undefined
        : Number(employment.probationMonths),
      nationality: person.nationality || undefined,
      sourceReference: 'added on screen',
    }))
    setCreatedId(detail.summary.id)
    setCreatedName(detail.summary.displayName)
    setStep(3)
  }

  async function saveAccount() {
    setBankError(null)
    try {
      await bank.save.mutateAsync(pruned({ ...account, primaryFlag: true }))
      setBankSaved(true)
    } catch (caught) {
      setBankError(toApiProblem(caught)?.detail ?? 'The bank account could not be saved.')
    }
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Add employee"
        breadcrumb={breadcrumb}
        subtitle="Who they are, what their contract says, where they sit — then anything else you have to hand."
      />

      <Card className="p-5">
        <ol className="mb-5 flex flex-wrap items-center gap-2 text-sm" aria-label="Steps">
          {STEPS.map((label, index) => (
            <li key={label} className="flex items-center gap-2">
              <span
                className={cn(
                  'grid size-6 place-items-center rounded-full text-xs',
                  index < step && 'bg-[var(--color-success)] text-white',
                  index === step && 'bg-[var(--color-primary)] text-white',
                  index > step && 'bg-[var(--color-surface)] text-[var(--color-muted)]',
                )}
                aria-current={index === step ? 'step' : undefined}
              >
                {index < step ? <Check className="size-3" aria-hidden="true" /> : index + 1}
              </span>
              <span className={cn(index === step
                ? 'text-[var(--color-heading)]'
                : 'text-[var(--color-muted)]')}
              >
                {label}
              </span>
              {index < STEPS.length - 1 && (
                <ArrowRight className="size-3 text-[var(--color-muted)]" aria-hidden="true" />
              )}
            </li>
          ))}
        </ol>

        {step === 0 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="first-name" label="First name" required
              value={person.firstName}
              error={fieldErrors.firstName}
              onChange={(value) => setPerson({ ...person, firstName: value })}
            />
            <TextField
              id="middle-name" label="Middle name"
              value={person.middleName}
              error={fieldErrors.middleName}
              onChange={(value) => setPerson({ ...person, middleName: value })}
            />
            <TextField
              id="last-name" label="Last name"
              hint="Leave blank for a single-name person; the profile then shows the first name alone."
              value={person.lastName}
              error={fieldErrors.lastName}
              onChange={(value) => setPerson({ ...person, lastName: value })}
            />
            <ChoiceField
              id="gender" label="Gender" choices={GENDERS}
              value={person.gender}
              onChange={(value) => setPerson({ ...person, gender: value })}
            />
            <Field id="date-of-birth" label="Date of birth">
              <DatePicker
                id="date-of-birth"
                value={person.dateOfBirth}
                onChange={(event) => setPerson({ ...person, dateOfBirth: event.target.value })}
              />
            </Field>
            <Field id="nationality" label="Nationality" error={fieldErrors.nationality}>
              <CountryPicker
                id="nationality"
                value={person.nationality}
                invalid={Boolean(fieldErrors.nationality)}
                onChange={(value) => setPerson({ ...person, nationality: value })}
              />
            </Field>
            <TextField
              id="national-id-type" label="National ID type"
              placeholder="Emirates ID"
              value={person.nationalIdType}
              error={fieldErrors.nationalIdType}
              onChange={(value) => setPerson({ ...person, nationalIdType: value })}
            />
            <TextField
              id="national-id" label="National ID"
              hint="Stored encrypted, and shown only when somebody asks for it."
              value={person.nationalId}
              error={fieldErrors.nationalId}
              onChange={(value) => setPerson({ ...person, nationalId: value })}
            />
            <TextField
              id="passport-no" label="Passport number"
              value={person.passportNo}
              error={fieldErrors.passportNo}
              onChange={(value) => setPerson({ ...person, passportNo: value })}
            />
            <TextField
              id="personal-email" label="Personal email" type="email"
              value={person.personalEmail}
              error={fieldErrors.personalEmail}
              onChange={(value) => setPerson({ ...person, personalEmail: value })}
            />
            <TextField
              id="personal-phone" label="Personal phone"
              value={person.personalPhone}
              error={fieldErrors.personalPhone}
              onChange={(value) => setPerson({ ...person, personalPhone: value })}
            />
          </div>
        )}

        {step === 1 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="employee-code" label="Employee code"
              hint="Leave blank to have one allocated from the entity's numbering series."
              value={employment.employeeCode}
              error={fieldErrors.employeeCode}
              onChange={(value) => setEmployment({ ...employment, employeeCode: value })}
            />
            <TextField
              id="work-email" label="Work email" type="email"
              hint="What they will sign in with once an account is created for them."
              value={employment.workEmail}
              error={fieldErrors.workEmail}
              onChange={(value) => setEmployment({ ...employment, workEmail: value })}
            />
            <TextField
              id="work-phone" label="Work phone"
              value={employment.workPhone}
              error={fieldErrors.workPhone}
              onChange={(value) => setEmployment({ ...employment, workPhone: value })}
            />
            <OptionField
              id="legal-entity" label="Legal entity"
              emptyLabel="Use the only one" options={entities.data}
              value={employment.legalEntityId}
              onChange={(value) => setEmployment({ ...employment, legalEntityId: value })}
            />
            <OptionField
              id="establishment" label="Establishment"
              emptyLabel="The entity's first" options={establishments.data}
              value={employment.establishmentId}
              onChange={(value) => setEmployment({ ...employment, establishmentId: value })}
            />
            <Field id="join-date" label="Join date" required
              hint="The first placement starts on this date."
            >
              <DatePicker
                id="join-date"
                value={employment.joinDate}
                onChange={(event) => setEmployment({ ...employment, joinDate: event.target.value })}
              />
            </Field>
            <ChoiceField
              id="employment-type" label="Employment type" choices={EMPLOYMENT_TYPES}
              emptyLabel="Full time"
              value={employment.employmentType}
              onChange={(value) => setEmployment({ ...employment, employmentType: value })}
            />
            <TextField
              id="contract-type" label="Contract type"
              placeholder="Limited"
              value={employment.contractType}
              error={fieldErrors.contractType}
              onChange={(value) => setEmployment({ ...employment, contractType: value })}
            />
            <Field id="contract-end" label="Contract end date"
              hint="For a fixed-term contract. Leave blank for an open-ended one."
            >
              <DatePicker
                id="contract-end"
                value={employment.contractEndDate}
                onChange={(event) => setEmployment({ ...employment, contractEndDate: event.target.value })}
              />
            </Field>
            <Field id="probation-months" label="Probation months">
              <Input
                id="probation-months"
                type="number"
                min={0}
                value={employment.probationMonths}
                onChange={(event) =>
                  setEmployment({ ...employment, probationMonths: event.target.value })}
              />
            </Field>
          </div>
        )}

        {step === 2 && (
          <div className="grid gap-4 sm:grid-cols-2">
            <OptionField
              id="department" label="Department" required
              emptyLabel="Choose a department" options={departments.data}
              value={placement.departmentId}
              onChange={(value) => setPlacement({ ...placement, departmentId: value })}
            />
            <OptionField
              id="division" label="Division" options={divisions.data}
              value={placement.divisionId}
              onChange={(value) => setPlacement({ ...placement, divisionId: value })}
            />
            <OptionField
              id="designation" label="Designation" options={designations.data}
              value={placement.designationId}
              onChange={(value) => setPlacement({ ...placement, designationId: value })}
            />
            <OptionField
              id="grade" label="Grade" options={grades.data}
              value={placement.gradeId}
              onChange={(value) => setPlacement({ ...placement, gradeId: value })}
            />
            <OptionField
              id="location" label="Location" options={locations.data}
              value={placement.locationId}
              onChange={(value) => setPlacement({ ...placement, locationId: value })}
            />
            <OptionField
              id="cost-center" label="Cost centre" options={costCenters.data}
              value={placement.costCenterId}
              onChange={(value) => setPlacement({ ...placement, costCenterId: value })}
            />
            <OptionField
              id="manager" label="Reports to"
              emptyLabel="Nobody yet"
              options={managers.data?.map((option) => ({
                id: option.id,
                name: `${option.displayName} (${option.employeeCode})`,
              }))}
              value={placement.managerId}
              onChange={(value) => setPlacement({ ...placement, managerId: value })}
            />
          </div>
        )}

        {step === 3 && createdId && (
          <div className="space-y-3">
            <p className="flex items-start gap-2 text-sm text-[var(--color-muted)]">
              <FileText className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              {createdName} is in the directory. Add whatever paperwork you have to hand — a
              passport, a visa, a contract. Anything you do not have yet can be added later
              from their profile.
            </p>
            <EmployeeDocumentsTab employeeId={createdId} />
          </div>
        )}

        {step === 4 && createdId && (
          <div className="space-y-4">
            <p className="flex items-start gap-2 text-sm text-[var(--color-muted)]">
              <CreditCard className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              Where their salary is paid. This becomes the primary account. Skip it if payroll
              has not been given the details yet — nothing else on the record depends on it.
            </p>
            <div className="grid gap-4 sm:grid-cols-2">
              <TextField
                id="bank-name" label="Bank"
                value={account.bankName}
                onChange={(value) => setAccount({ ...account, bankName: value })}
              />
              <TextField
                id="account-holder" label="Account holder"
                hint="If it differs from the employee's own name."
                value={account.accountHolderName}
                onChange={(value) => setAccount({ ...account, accountHolderName: value })}
              />
              <TextField
                id="iban" label="IBAN"
                value={account.iban}
                onChange={(value) => setAccount({ ...account, iban: value })}
              />
              <TextField
                id="account-number" label="Account number"
                hint="An IBAN or an account number — one of the two is enough."
                value={account.accountNumber}
                onChange={(value) => setAccount({ ...account, accountNumber: value })}
              />
              <TextField
                id="swift" label="SWIFT / BIC"
                value={account.swiftCode}
                onChange={(value) => setAccount({ ...account, swiftCode: value })}
              />
              <TextField
                id="routing" label="Routing code"
                value={account.routingCode}
                onChange={(value) => setAccount({ ...account, routingCode: value })}
              />
              <TextField
                id="currency" label="Currency"
                placeholder="AED"
                value={account.currency}
                onChange={(value) => setAccount({ ...account, currency: value })}
              />
            </div>
            {bankError && (
              <p role="alert" className="text-sm text-[var(--color-danger)]">{bankError}</p>
            )}
            {bankSaved && (
              <p className="text-sm text-[var(--color-success)]">Bank account saved.</p>
            )}
          </div>
        )}

        {createError && (
          <div role="alert" className="mt-4 space-y-1 text-sm text-[var(--color-danger)]">
            <p>{createError}</p>
            {Object.keys(fieldErrors).length > 0 && (
              <ul className="list-disc ps-5">
                {Object.entries(fieldErrors).map(([field, message]) => (
                  <li key={field}>
                    <span className="font-medium">{FIELD_LABELS[field] ?? field}:</span> {message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="mt-5 flex items-center justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={() => {
              if (step === 0 || createdId) {
                void navigate(createdId ? `/employees/${createdId}` : '/employees')
              } else {
                setStep(step - 1)
              }
            }}
          >
            {step === 0 ? 'Cancel' : createdId ? 'Open the profile' : 'Back'}
          </Button>

          <div className="flex gap-2">
            {step === 0 && (
              <Button type="button" disabled={!canLeavePerson} onClick={() => setStep(1)}>
                Next: employment
              </Button>
            )}
            {step === 1 && (
              <Button type="button" disabled={!canLeaveEmployment} onClick={() => setStep(2)}>
                Next: placement
              </Button>
            )}
            {step === 2 && (
              <Button
                type="button"
                disabled={!canCreate || create.isPending}
                onClick={() => { void createEmployee() }}
              >
                {create.isPending ? 'Creating…' : 'Create employee'}
              </Button>
            )}
            {step === 3 && (
              <Button type="button" onClick={() => setStep(4)}>Next: bank</Button>
            )}
            {step === 4 && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  disabled={account.bankName.trim() === '' || bank.save.isPending}
                  onClick={() => { void saveAccount() }}
                >
                  {bank.save.isPending ? 'Saving…' : 'Save bank account'}
                </Button>
                <Button
                  type="button"
                  onClick={() => { void navigate(`/employees/${createdId!}`) }}
                >
                  Finish
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>
    </div>
  )
}
